import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import { Section, Overline } from "@/components/site/Section";
import CtaBanner from "@/components/site/CtaBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function formatDate(iso) {
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
}

function BlogHero() {
  return (
    <Section className="pb-12">
      <Overline className="mb-5">Insights</Overline>
      <h1 className="font-display text-5xl md:text-6xl tracking-tight leading-[1.05] max-w-4xl">
        Hiring intelligence for <span className="italic text-[hsl(var(--primary))]">modern teams.</span>
      </h1>
      <p className="mt-6 max-w-3xl text-lg md:text-xl text-muted-foreground">
        Practical thinking on recruiting, RPO, and HR strategy — from the team that builds
        high-performing workforces every day.
      </p>
    </Section>
  );
}

function FeaturedCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-featured-${post.slug}`}
      className="group grid grid-cols-1 lg:grid-cols-12 gap-0 bg-[hsl(var(--secondary))]/40 border border-border rounded-xl overflow-hidden hover:border-[hsl(var(--primary))]/50 transition-colors"
    >
      <div className="lg:col-span-7 aspect-[16/9] lg:aspect-auto overflow-hidden">
        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500" />
      </div>
      <div className="lg:col-span-5 p-8 md:p-10 flex flex-col justify-center">
        <div className="flex items-center gap-3 text-xs">
          <span className="px-2.5 py-1 rounded-md bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-semibold uppercase tracking-wider">{post.category}</span>
          <span className="text-muted-foreground">{formatDate(post.created_at)}</span>
        </div>
        <h2 className="mt-4 font-display text-2xl md:text-3xl leading-tight group-hover:text-[hsl(var(--primary))] transition-colors">{post.title}</h2>
        <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{post.excerpt}</p>
        <span className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-[hsl(var(--primary))]">
          Read article <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
        </span>
      </div>
    </Link>
  );
}

function PostCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group bg-[hsl(var(--background))] border border-border rounded-xl overflow-hidden hover:border-[hsl(var(--primary))]/50 hover:-translate-y-1 transition-all"
    >
      <div className="aspect-[16/9] overflow-hidden">
        <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500" />
      </div>
      <div className="p-6 md:p-7">
        <div className="flex items-center gap-3 text-xs">
          <span className="px-2.5 py-1 rounded-md bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-semibold uppercase tracking-wider">{post.category}</span>
          <span className="text-muted-foreground">{formatDate(post.created_at)}</span>
        </div>
        <h3 className="mt-4 font-display text-xl leading-tight group-hover:text-[hsl(var(--primary))] transition-colors">{post.title}</h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-3">{post.excerpt}</p>
      </div>
    </Link>
  );
}

export default function Blog() {
  const [posts, setPosts] = useState(null);

  useEffect(() => {
    document.title = "Blog & Insights | X-HRverse";
    axios.get(`${API}/posts`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
    return () => { document.title = "X-HRverse | Building High-Performing Teams for the Modern Workforce"; };
  }, []);

  const [featured, ...rest] = posts || [];

  return (
    <div data-testid="page-blog">
      <BlogHero />
      <Section className="pt-0">
        {posts === null ? (
          <p className="text-muted-foreground" data-testid="blog-loading">Loading articles...</p>
        ) : posts.length === 0 ? (
          <p className="text-muted-foreground" data-testid="blog-empty">No articles published yet — check back soon.</p>
        ) : (
          <>
            <FeaturedCard post={featured} />
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((p) => <PostCard key={p.id} post={p} />)}
            </div>
          </>
        )}
      </Section>
      <CtaBanner
        title="Rather talk it through than read about it?"
        ctaLabel="Schedule a Consultation"
        testid="blog-cta"
        topPadding={false}
      />
    </div>
  );
}
